import { CalendarOutlined } from '@ant-design/icons';
import { EditorContent, useEditor } from '@tiptap/react';
import { Tag } from 'antd';
import dayjs from 'dayjs';
import { useEffect } from 'react';

import { CommentStatusTag } from 'components/comments/CommentStatusTag';
import { getDisplayEditorExtensions } from 'components/comments/utils/editorConfig';

import type { Comment, CommentReply } from 'types/task/TaskComments';

interface CommentNotificationDisplayProps {
  comment: Comment | CommentReply;
}

export function CommentNotificationDisplay({
  comment,
}: CommentNotificationDisplayProps) {
  const displayContent = useEditor({
    extensions: getDisplayEditorExtensions(),
    content: comment.content,
    editable: false,
  });

  // Check if this is an assigned comment (has assignee)
  const isAssignedComment = 'assignee' in comment && comment.assignee !== null;
  const hasDueDate = 'due_datetime' in comment && comment.due_datetime;
  const statusChange =
    'status_change_because_of_reply' in comment
      ? comment.status_change_because_of_reply
      : undefined;

  useEffect(() => {
    displayContent?.commands.setContent(comment.content);
  }, [comment.content, displayContent]);

  if (!displayContent) return null;

  // Assigned comments: show background, border, and due date
  if (isAssignedComment) {
    return (
      <div className="border-l-3 border-color-border pl-[10px]">
        <div className="bg-color-fill-alter-solid py-3 px-4 rounded-lg border-color-border border">
          <EditorContent
            editor={displayContent}
            className="mb-2 text-sm [&_ul]:pl-4 [&_ol]:pl-4 [&_li]:pl-2 [&_.ProseMirror]:outline-none [&_.ProseMirror]:wrap-break-words [&_.ProseMirror]:break-all"
          />
          {/* Show due date for assigned comments */}
          {hasDueDate && dayjs(comment.due_datetime).isValid() && (
            <div className="mt-1">
              <Tag
                variant="outlined"
                className="bg-mauve-grey-1 text-mauve-grey-8 border-mauve-grey-3"
                icon={<CalendarOutlined />}
              >
                Due {dayjs(comment.due_datetime).format('MMM DD, YYYY')}
              </Tag>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Regular comment styling - just the comment text with @mentions
  return (
    <div className="border-l-3 border-color-border pl-[10px]">
      <div className="text-sm text-color-text-secondary">
        <CommentStatusTag
          status={statusChange}
          createdAt={comment.created_at}
        />
        <EditorContent
          editor={displayContent}
          className="[&_ul]:pl-4 [&_ol]:pl-4 [&_li]:pl-2 [&_.ProseMirror]:outline-none [&_.ProseMirror]:wrap-break-words [&_.ProseMirror]:break-all"
        />
      </div>
    </div>
  );
}
